<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import type { VoiceNote } from "@/types/album";

import { formatClock, formatDuration } from "@/lib/format";
import { MOMENT_LABELS } from "@/types/album";
import { useGuestbookStore } from "@/stores/guestbook";

/**
 * Visionneuse d'un message vocal, calquée sur `MediaLightbox` : un message
 * s'ouvre en grand comme une vidéo, avec sa lecture, sa progression et sa
 * suppression. Rien ne se joue depuis la vignette — la carte de la mosaïque
 * ouvre, la visionneuse lit.
 */

const props = defineProps<{ note: VoiceNote | null }>();
const emit = defineEmits<{ close: []; delete: [id: string] }>();

const guestbook = useGuestbookStore();

const dialog = ref<HTMLDialogElement | null>(null);
const audio = ref<HTMLAudioElement | null>(null);

const isPlaying = ref(false);
const currentMs = ref(0);

const durationLabel = computed(() =>
  props.note ? formatDuration(props.note.durationMs) : "00:00",
);
const positionLabel = computed(() => formatDuration(currentMs.value));
const momentLabel = computed(() => (props.note?.moment ? MOMENT_LABELS[props.note.moment] : ""));

/** Part écoulée, bornée : la durée mesurée à l'enregistrement peut dériver. */
const progress = computed(() => {
  if (!props.note?.durationMs) return 0;
  return Math.min(1, currentMs.value / props.note.durationMs);
});

/**
 * Barres d'onde décoratives, dérivées de l'identifiant du message : la
 * silhouette reste la même que sur la carte de la mosaïque.
 */
const bars = computed(() => {
  const id = props.note?.id ?? "";
  const seed = [...id].reduce((total, char) => total + char.charCodeAt(0), 0);
  return Array.from({ length: 48 }, (_, index) => {
    const wave = Math.sin((seed + index * 37) * 0.7);
    return 18 + Math.abs(wave) * 68;
  });
});

/** Nombre de barres déjà parcourues, pour colorer l'onde au fil de la lecture. */
const playedBars = computed(() => Math.round(progress.value * bars.value.length));

function stop(): void {
  audio.value?.pause();
  isPlaying.value = false;
  currentMs.value = 0;
}

function toggle(): void {
  const node = audio.value;
  if (!node) return;
  if (isPlaying.value) {
    node.pause();
    isPlaying.value = false;
    return;
  }
  isPlaying.value = true;
  node.play().catch(() => {
    isPlaying.value = false;
  });
}

function onTimeUpdate(): void {
  currentMs.value = (audio.value?.currentTime ?? 0) * 1000;
}

function onEnded(): void {
  isPlaying.value = false;
  currentMs.value = 0;
}

/** Repositionne la lecture au point cliqué sur l'onde. */
function seekTo(event: MouseEvent): void {
  const node = audio.value;
  const track = event.currentTarget as HTMLElement | null;
  if (!node || !track || !props.note?.durationMs) return;
  const bounds = track.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
  node.currentTime = (props.note.durationMs / 1000) * ratio;
  onTimeUpdate();
}

watch(
  () => props.note,
  (note) => {
    const node = dialog.value;
    if (!node) return;
    stop();
    if (note) {
      const url = guestbook.urlFor(note.id);
      if (audio.value && url) audio.value.src = url;
      if (!node.open) node.showModal();
    } else if (node.open) {
      node.close();
    }
  },
  { flush: "post" },
);

onBeforeUnmount(stop);
</script>

<template>
  <dialog
    ref="dialog"
    class="voice-preview"
    data-testid="voice-preview"
    aria-label="Message du livre d’or"
    @close="emit('close')"
    @cancel.prevent="emit('close')"
  >
    <div v-if="note" class="voice-preview__body">
      <audio ref="audio" preload="metadata" @timeupdate="onTimeUpdate" @ended="onEnded"></audio>

      <button
        type="button"
        class="voice-preview__close"
        data-testid="voice-preview-close"
        aria-label="Fermer"
        @click="emit('close')"
      >
        ×
      </button>

      <div class="voice-preview__stage">
        <button
          type="button"
          class="voice-preview__play"
          data-testid="voice-preview-play"
          :aria-label="isPlaying ? 'Mettre en pause' : 'Écouter le message'"
          :aria-pressed="isPlaying"
          @click="toggle"
        >
          <svg v-if="!isPlaying" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9 7 8 5-8 5V7Z" fill="currentColor" stroke="none"></path>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 6h3v12H8zM13 6h3v12h-3z" fill="currentColor" stroke="none"></path>
          </svg>
        </button>

        <!-- Piste cliquable : l'onde sert aussi de barre de progression. -->
        <div class="voice-preview__wave" data-testid="voice-preview-wave" @click="seekTo">
          <i
            v-for="(height, barIndex) in bars"
            :key="barIndex"
            :class="{ 'is-played': barIndex < playedBars }"
            :style="{ height: `${height}%` }"
          ></i>
        </div>

        <p class="voice-preview__time">
          <span data-testid="voice-preview-position">{{ positionLabel }}</span>
          <span>{{ durationLabel }}</span>
        </p>
      </div>

      <div class="voice-preview__info">
        <div>
          <strong>Un mot pour les mariés</strong>
          <span
            ><b>{{ note.author }}</b> <template v-if="momentLabel"> · {{ momentLabel }}</template> ·
            <time>{{ formatClock(note.createdAt) }}</time></span
          >
        </div>
        <button
          type="button"
          class="voice-preview__action"
          data-testid="voice-preview-delete"
          :aria-label="`Supprimer le message de ${note.author}`"
          @click="emit('delete', note.id)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 7h14M9 7V5h6v2m-9 0 1 13h10l1-13" stroke-linejoin="round"></path>
          </svg>
        </button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.voice-preview {
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  padding: 0;
  color: white;
  border: 0;
  background: rgb(18 22 19 / 0.96);
}

.voice-preview::backdrop {
  background: rgb(12 15 13 / 0.88);
}

.voice-preview__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 24px;
}

.voice-preview__close {
  position: absolute;
  top: 18px;
  right: 18px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.25);
  border-radius: 50%;
  background: rgb(255 255 255 / 0.08);
  font-size: 20px;
}

.voice-preview__stage {
  width: min(100%, 620px);
  margin: auto;
  text-align: center;
}

.voice-preview__play {
  display: grid;
  width: 84px;
  height: 84px;
  margin: 0 auto 34px;
  place-items: center;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.35);
  border-radius: 50%;
  background: var(--color-olive);
  box-shadow: 0 0 0 12px rgb(255 255 255 / 0.05);
}

.voice-preview__play svg {
  width: 34px;
}

.voice-preview__wave {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 130px;
  cursor: pointer;
}

.voice-preview__wave i {
  display: block;
  width: 4px;
  border-radius: 99px;
  background: rgb(255 255 255 / 0.28);
  transition: background-color 0.15s ease;
}

.voice-preview__wave i.is-played {
  background: var(--color-rose-light, #e8bfae);
}

.voice-preview__time {
  display: flex;
  justify-content: space-between;
  margin: 14px 0 0;
  color: rgb(255 255 255 / 0.65);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.voice-preview__info {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  width: min(100%, 620px);
  margin: 0 auto;
}

.voice-preview__info strong {
  display: block;
  margin-bottom: 6px;
  font-family: var(--font-script);
  font-size: 31px;
  font-weight: 400;
}

.voice-preview__info span {
  color: rgb(255 255 255 / 0.62);
  font-size: 13px;
}

.voice-preview__info b {
  font-weight: 600;
}

.voice-preview__action {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.25);
  border-radius: 50%;
  background: rgb(255 255 255 / 0.08);
}

.voice-preview__action svg {
  width: 19px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.5;
}
</style>
